import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, TextField, MenuItem} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {sectionCardSx, primaryBtnSx, secondaryBtnSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, INFO} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {int} from '../format'
import {MAX_SUBMISSIONS, submissionsUsed} from './logic'
import {SUBMIT_KINDS} from './copy'
import RqCounter from './RqCounter'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const bannerTextSx = {fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}
const fieldSx = {fontFamily: FONT, fontSize: SIZE.xs}

/**
 * Weight Freeze (v2) — Requests: the additional-data submission drawer.
 *
 * `kindLabel` is resolved from SUBMIT_KINDS BEFORE it leaves this drawer, so
 * `requests/logic.js`'s own `applySubmission` never has to know the option
 * list — it only ever sees `{kindLabel, ships, note}`. Fields reset on every
 * open, keyed on the request id, so a stale draft from a previous request
 * (or a previous round on the same one) never carries over.
 */
export default function RqSubmitDrawer({req, open, onClose, onSubmit}) {
  const [kind, setKind] = React.useState(SUBMIT_KINDS[0].v)
  const [ships, setShips] = React.useState(10)
  const [note, setNote] = React.useState('')

  const reqId = req && req.id
  React.useEffect(() => {
    if (open) { setKind(SUBMIT_KINDS[0].v); setShips(10); setNote('') }
  }, [open, reqId])

  if (!req) return null

  const used = submissionsUsed(req)
  const next = Math.min(MAX_SUBMISSIONS, used + 1)
  const isLast = next >= MAX_SUBMISSIONS
  const kindLabel = (SUBMIT_KINDS.find((k) => k.v === kind) || SUBMIT_KINDS[0]).l

  const handleSend = () => {
    onSubmit({kindLabel, ships: Number(ships) || 0, note: note.trim()})
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 560}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Submit additional data
              </Typography>
              <Typography sx={labelSx}>{req.id} · {req.subject}</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close submission drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
            <Typography sx={bannerTextSx}>
              This is submission <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{next} of {MAX_SUBMISSIONS}</Box>{' '}
              on this request. {isLast
                ? 'It is the last one. After it is sent, Prozo decides on the evidence on file and the request cannot be added to again.'
                : 'One additional submission will remain after this one.'}
            </Typography>
          </Box>

          <Box sx={{...sectionCardSx, display: 'grid', gap: GAP.md}}>
            <TextField
              select
              size='small'
              label='What are you sending'
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              helperText='Prozo sees this as the headline on your submission.'
              sx={fieldSx}
            >
              {SUBMIT_KINDS.map((k) => <MenuItem key={k.v} value={k.v}>{k.l}</MenuItem>)}
            </TextField>

            <TextField
              size='small'
              type='number'
              label='Shipments covered'
              value={ships}
              onChange={(event) => setShips(event.target.value)}
              helperText={`This request currently carries ${int(req.evidence || 0)} DWS images as evidence.`}
              inputProps={{min: 1, max: 500}}
              sx={fieldSx}
            />

            <TextField
              size='small'
              multiline
              rows={4}
              label='Note to Prozo'
              value={note}
              onChange={(event) => setNote(event.target.value)}
              helperText='Optional. Anything that explains what changed since the last round.'
              placeholder='For example: the heavier variant now ships in the medium carton, not the large one.'
              sx={fieldSx}
            />
          </Box>

          <RqCounter used={used} />

          <Typography sx={labelSx}>
            This demo captures the headline, a shipment count and a note. A real submission would attach
            the files themselves.
          </Typography>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={primaryBtnSx} onClick={handleSend}>Send submission {next} of {MAX_SUBMISSIONS}</Button>
        </Box>
      </Box>
    </Drawer>
  )
}
